import i18n from '@/i18n';
import moment, { MomentInput } from 'moment/moment';
import {
  DEFAULT_BAR_COLOR,
  DEFAULT_BAR_MAX_WIDTH,
  DEFAULT_ECHART_COLORS,
  DEFAULT_ECHART_XAXIS_CONFIG,
  DEFAULT_ECHART_YAXIS_CONFIG,
  DEFAULT_LEGEND_COLOR,
  DEFAULT_TEXT_COLOR,
  DEFUALT_SPLIT_NUMBER, undone,
} from '@/infrastructure/echart-config/index';
import { formatDashboardTimestamp } from '@/infrastructure/utils/timestamp';
import { TimeType } from '@/proto/metric';

/**
 * metric chart
 * @param series
 * @param seriesUnit
 * @param counts
 * @param countsUnit
 * @param timestamps
 * @param timeWindow
 * @param tooltipFormatter
 * @param colors
 * @param legend
 * @param showCount
 */
export const getMetricEChartOptions = (
  series: { name: string; data: (string | number)[]; }[],
  seriesUnit: string,
  counts: number[],
  countsUnit: string,
  timestamps: string[],
  timeWindow?: TimeType,
  tooltipFormatter?: (params: any) => string,
  colors?: string[],
  legend?: Record<string, any>,
  showCount = true,
  seriesColors?: string[],
) => {
  const commonTooltipFormatter = (params: any) => {
    if (params instanceof Array && params.length > 0) {
      const momentDate = moment(params[0]?.axisValue);
      const isTimeTypeHour = TimeType.HOUR === timeWindow;
      const dateStr = `${TimeType.DAY === timeWindow ? momentDate.format('YYYY-MM-DD') : i18n.t('统计区间：[{{start}}, {{end}})', { start: momentDate.format('HH:mm'), end: momentDate.add(isTimeTypeHour ? 1 : 5, isTimeTypeHour ? 'hours' : 'minutes').format('HH:mm') })}`;
      const info = params.map((param) => {
        const unitLabel = param.seriesName?.indexOf(i18n.t('上报量')) !== -1 ? ` ${countsUnit}` : ` ${seriesUnit}`;
        return `<br/>${param.marker}${param.seriesName}: ${param.value || 0} ${unitLabel}`;
      }).join('');
      return `${dateStr}${info}`;
    }
    return '';
  };

  const legendData = series.map(entry => entry.name);

  if (showCount) {
    legendData.push(i18n.t('上报量'));
  }

  const emphasisColors = seriesColors || DEFAULT_ECHART_COLORS;

  return {
    color: colors ? colors : [...DEFAULT_ECHART_COLORS.slice(0, series.length), DEFAULT_BAR_COLOR],
    xAxis: {
      ...DEFAULT_ECHART_XAXIS_CONFIG,
      axisLabel: {
        ...DEFAULT_ECHART_XAXIS_CONFIG.axisLabel,
        formatter: (value: MomentInput) => formatDashboardTimestamp(value, timeWindow),
      },
      data: timestamps,
    },
    yAxis: [
      {
        ...DEFAULT_ECHART_YAXIS_CONFIG,
        name: `${i18n.t('单位')}（${seriesUnit}）`,
        splitNumber: DEFUALT_SPLIT_NUMBER,
        alignTicks: true,
      },
      {
        ...DEFAULT_ECHART_YAXIS_CONFIG,
        name: `${i18n.t('上报量')}${countsUnit ? `（${countsUnit}）` : ''}`,
        splitNumber: DEFUALT_SPLIT_NUMBER,
        alignTicks: true,
      },
    ],
    grid: {
      left: 60,
      right: 60,
    },
    legend: {
      textStyle: {
        color: DEFAULT_LEGEND_COLOR,
      },
      data: legendData,
      ...legend,
    },
    textStyle: {
      color: DEFAULT_TEXT_COLOR,
    },
    tooltip: {
      trigger: 'axis',
      formatter: tooltipFormatter ?? commonTooltipFormatter,
    },
    series: [
      ...series.map((item, index) => ({
        type: 'line',
        showSymbol: true,
        symbol: 'circle',
        emphasis: {
          itemStyle: {
            borderColor: emphasisColors[index],
            color: '#FFFFFF',
          },
        },
        name: item.name,
        data: item.data,
        yAxisIndex: 0,
        unit: seriesUnit,
        symbolSize: 6,
        itemStyle: {
          color(params: { name: moment.MomentInput; color: string; }) {
            return undone(params, timeWindow);
          },
        },
      })),
      {
        type: 'bar',
        name: i18n.t('上报量'),
        data: counts,
        yAxisIndex: 1,
        unit: countsUnit,
        barMaxWidth: DEFAULT_BAR_MAX_WIDTH,
      },
    ],
  };
};

/**
 * metric chart
 * @param series
 * @param seriesUnit
 * @param counts
 * @param countsUnit
 * @param timestamps
 * @param timeWindow
 * @param tooltipFormatter
 * @param colors
 * @param legend
 * @param showCount
 */
export const getMetricEChartOptionsWithCustomUnit = (
  series: { name: string; data: (string | number)[]; }[],
  seriesUnit: string,
  counts: number[],
  countsUnit: string,
  timestamps: string[],
  timeWindow?: TimeType,
  tooltipFormatter?: (params: any) => string,
  colors?: string[],
  legend?: Record<string, any>,
  showCount = true,
  seriesColors?: string[],
) => {
  const commonTooltipFormatter = (params: any) => {
    if (params instanceof Array && params.length > 0) {
      const momentDate = moment(params[0]?.axisValue);
      const isTimeTypeHour = TimeType.HOUR === timeWindow;
      const dateStr = `${TimeType.DAY === timeWindow ? momentDate.format('YYYY-MM-DD') : i18n.t('统计区间：[{{start}}, {{end}})', { start: momentDate.format('HH:mm'), end: momentDate.add(isTimeTypeHour ? 1 : 5, isTimeTypeHour ? 'hours' : 'minutes').format('HH:mm') })}`;
      const info = params.map((param) => {
        const unitLabel = param.seriesName?.indexOf(i18n.t('上报量')) !== -1 ? ` ${countsUnit}` : ` ${seriesUnit}`;
        return `<br/>${param.marker}${param.seriesName}: ${param.value || 0} ${unitLabel}`;
      }).join('');
      return `${dateStr}${info}`;
    }
    return '';
  };

  const legendData = series.map(entry => entry.name);

  if (showCount) {
    legendData.push(i18n.t('上报量'));
  }

  const emphasisColors = seriesColors || DEFAULT_ECHART_COLORS;

  return {
    color: colors ? colors : [...DEFAULT_ECHART_COLORS.slice(0, series.length), DEFAULT_BAR_COLOR],
    xAxis: {
      ...DEFAULT_ECHART_XAXIS_CONFIG,
      axisLabel: {
        ...DEFAULT_ECHART_XAXIS_CONFIG.axisLabel,
        formatter: (value: MomentInput) => formatDashboardTimestamp(value, timeWindow),
      },
      data: timestamps,
    },
    yAxis: [
      {
        ...DEFAULT_ECHART_YAXIS_CONFIG,
        name: `${i18n.t('单位')}（${seriesUnit}）`,
        splitNumber: DEFUALT_SPLIT_NUMBER,
        alignTicks: true,
      },
      {
        ...DEFAULT_ECHART_YAXIS_CONFIG,
        name: `${i18n.t('上报量')}${countsUnit ? `（${countsUnit}）` : ''}`,
        splitNumber: DEFUALT_SPLIT_NUMBER,
        alignTicks: true,
      },
    ],
    grid: {
      left: 60,
      right: 60,
    },
    legend: {
      textStyle: {
        color: DEFAULT_LEGEND_COLOR,
      },
      data: legendData,
      ...legend,
    },
    textStyle: {
      color: DEFAULT_TEXT_COLOR,
    },
    tooltip: {
      trigger: 'axis',
      formatter: tooltipFormatter ?? commonTooltipFormatter,
    },
    series: [
      ...series.map((item, index) => ({
        type: 'line',
        showSymbol: true,
        symbol: 'circle',
        emphasis: {
          itemStyle: {
            borderColor: emphasisColors[index],
            color: '#FFFFFF',
          },
        },
        name: item.name,
        data: item.data,
        yAxisIndex: 0,
        unit: seriesUnit,
        symbolSize: 6,
        itemStyle: {
          color(params: { name: moment.MomentInput; color: string; }) {
            return undone(params, timeWindow);
          },
        },
      })),
      {
        type: 'bar',
        name: i18n.t('上报量'),
        data: counts,
        yAxisIndex: 1,
        unit: countsUnit,
        barMaxWidth: DEFAULT_BAR_MAX_WIDTH,
      },
    ],
  };
};
